import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import { Button } from "@circle-react-uikit/Button";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { useCanDowngrade } from "../hooks/useCanDowngrade";
import type { UpgradePlan } from "../types";
import { BlockingFeatures } from "./BlockingFeatures";

const i18nRoot = "pricing_plans.upgrade_modal";

interface ScheduledDowngradeConfirmationModalProps {
  hideModal: () => void;
  onSubmitHandler: () => void;
  plan: UpgradePlan;
}

export const ScheduledDowngradeConfirmationModal = ({
  hideModal,
  onSubmitHandler,
  plan,
}: ScheduledDowngradeConfirmationModalProps) => {
  const { currentCommunity } = usePunditUserContext();

  const { data: canDowngradeData, isLoading: isCanDowngradeLoading } =
    useCanDowngrade(plan?.id);

  const formattedDate = format(
    new Date(currentCommunity?.subscription.current_period_end || ""),
    "MMM dd, yyyy",
  );

  const blockingFeatures = canDowngradeData?.blocking_features || [];

  return (
    <>
      <Modal.Header>
        <Modal.Title>
          {t([i18nRoot, "interval_switch.scheduled_downgrade.title"])}
        </Modal.Title>

        <Modal.HeaderActions>
          <Modal.CloseButton onClick={hideModal} />
        </Modal.HeaderActions>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-6 space-y-2">
          <Typography.BodyMd>
            {t(`${i18nRoot}.interval_switch.scheduled_downgrade.plan_expires`)}{" "}
            <strong className="font-semibold text-gray-900">
              {formattedDate}
            </strong>
            .
          </Typography.BodyMd>
        </div>
        <BlockingFeatures
          blockingFeatures={blockingFeatures}
          i18nRoot={i18nRoot}
          isLoading={isCanDowngradeLoading}
        />
      </Modal.Body>
      <Modal.Footer className="mt-5 flex justify-end gap-x-3">
        <Button variant="secondary" large onClick={hideModal}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          large
          onClick={onSubmitHandler}
          disabled={isCanDowngradeLoading}
        >
          {t([i18nRoot, "actions.scheduled_interval_downgrade"])}
        </Button>
      </Modal.Footer>
    </>
  );
};
